import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { flow, pickBy, identity, tap, keys, some, omit } from 'lodash/fp';
import { useModalProviderContext } from '../ModalProvider';
import { installationOnlyModal } from '../ModalProvider/modalNames';
import cartAsMandiJob from '../../services/cartAsMandiJob';
import { REASON_CODE_OPTIONS } from '../InstallationModalCommon/JobOptions/JobOptions';

import ResponsiveModal from '../modals';
import {
  FlexModal,
  FlexJobOptions,
  FlexAddToCartButton,
  FlexInstallationOptions,
  FlexAddToCartButtonContainer,
  FlexProjectDetails,
} from './styles';
import useInstallationState from './useInstallationOnlyState';

const InstallationOnlyModal = ({
  designId,
  containers,
  updatePricing,
  setLoading,
  globalError,
  textOptInNumber,
}) => {
  const { isOpen } = useModalProviderContext(installationOnlyModal);

  const wrappedUpdatePricing = useCallback((leafItems, opts) => {
    const pricingOpts = omit(['isReinstall', 'reasonCode'], opts);
    if (opts.isReinstall && opts.reasonCode) {
      const selected = REASON_CODE_OPTIONS.find(o => o.value === opts.reasonCode);
      if (selected) pricingOpts.reinstall = selected.code;
    }
    return updatePricing(leafItems, pricingOpts);
  }, [updatePricing]);

  const {
    selectedItems,
    toggleItem,
    toggleAll,
    options,
    setOption,
    pricing,
  } = useInstallationState(
    isOpen,
    designId,
    containers,
    wrappedUpdatePricing,
    {
      windowHaul: false,
      windowRemoval: false,
      windowAdjustment: false,
      textOptIn: true,
      textOptInNumber,
      specialInstructions: '',
      reworkType: 'install-only',
      isReinstall: false,
      reasonCode: 0,
    },
    ['windowHaul', 'windowRemoval', 'windowAdjustment', 'isReinstall', 'reasonCode'],
  );

  const submitEnabled = useMemo(() => {
    if (!some(identity, selectedItems)) return false;
    if (options.isReinstall && !options.reasonCode) return false;
    return true;
  }, [selectedItems, options.isReinstall, options.reasonCode]);

  const addToCart = useCallback(() => {
    setLoading(true);

    const leafItems = flow(
      pickBy(identity),
      keys,
    )(selectedItems);

    const submitOptions = { ...options };
    if (submitOptions.isReinstall && submitOptions.reasonCode) {
      const selected = REASON_CODE_OPTIONS.find(o => o.value === submitOptions.reasonCode);
      if (selected) {
        submitOptions.reinstall = selected.code;
      }
    }
    delete submitOptions.isReinstall;
    delete submitOptions.reasonCode;

    cartAsMandiJob({
      designId,
      leafItems,
      options: {
        createAndSaveJob: submitOptions,
        cartProducts: false,
      },
    })
      .then(tap(() => setLoading(false)))
      .catch(globalError);
  }, [
    designId,
    setLoading,
    globalError,
    JSON.stringify(options),
    JSON.stringify(selectedItems),
  ]);

  return (
    <ResponsiveModal
      id="installationOnlyModal"
      title="Installation Only"
      modalName={installationOnlyModal}
    >
      <FlexModal>
        <FlexProjectDetails>
          <FlexJobOptions
            id="jobOptionsPanel"
            {...options}
            onOptionChange={setOption}
            isInstallOnly
          />
          <FlexAddToCartButtonContainer>
            <FlexAddToCartButton id="addToCartButton" onClick={addToCart} disabled={!submitEnabled} />
          </FlexAddToCartButtonContainer>
        </FlexProjectDetails>
        <FlexInstallationOptions
          id="installationOptionsPanel"
          designId={designId}
          selectedItems={selectedItems}
          toggleItem={toggleItem}
          toggleAll={toggleAll}
          pricing={pricing}
        />
      </FlexModal>
    </ResponsiveModal>
  );
};

InstallationOnlyModal.propTypes = {
  designId: PropTypes.string.isRequired,
  containers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  updatePricing: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  globalError: PropTypes.func.isRequired,
  textOptInNumber: PropTypes.string.isRequired,
};
export default InstallationOnlyModal;
